import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Crown, Loader2, ShieldCheck, ChevronLeft, Delete } from "lucide-react";
import { toast } from "sonner";
import { BrandMarkLink } from "../components/BrandMarkLink";
import { PaperGrain } from "../components/PaperGrain";
import { api, TOKEN_KEY } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { homeForRole } from "../lib/roleHome";

/* ============================================================ STATE 1 */

const RolePicker = ({ onPickStaff, onPickAdmin }) => (
  <div data-testid="staff-login-role-picker" className="w-full">
    <p className="font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-gold text-center">
      Operational access
    </p>
    <h1 className="mt-3 font-display font-bold text-[26px] sm:text-[28px] text-napoli-cream text-center leading-tight">
      Choose your role.
    </h1>
    <p className="mt-3 font-body italic text-[13px] text-napoli-cream/70 text-center">
      PIN-based login for staff and management.
    </p>

    <div className="mt-10 flex flex-col gap-4">
      <button
        type="button"
        data-testid="role-picker-staff"
        onClick={onPickStaff}
        className="group flex items-center gap-5 w-full bg-napoli-oxblood text-napoli-cream border border-napoli-oxblood-dark px-6 py-5 hover:bg-napoli-oxblood-dark transition-colors text-left"
      >
        <ShieldCheck size={24} strokeWidth={1.6} className="text-napoli-gold flex-shrink-0" aria-hidden />
        <span className="flex-1 min-w-0">
          <span className="block font-display font-semibold text-[18px] leading-tight">Staff</span>
          <span className="mt-1 block font-body text-[11px] text-napoli-cream/70">Server, host, bartender</span>
        </span>
        <ArrowRight size={20} className="flex-shrink-0 transition-transform group-hover:translate-x-1" aria-hidden />
      </button>

      <button
        type="button"
        data-testid="role-picker-admin"
        onClick={onPickAdmin}
        className="group flex items-center gap-5 w-full bg-napoli-cream-warm text-napoli-oxblood border border-napoli-gold px-6 py-5 hover:bg-napoli-marble hover:border-napoli-gold-light transition-colors text-left"
      >
        <Crown size={24} strokeWidth={1.6} className="text-napoli-gold flex-shrink-0" aria-hidden />
        <span className="flex-1 min-w-0">
          <span className="block font-display font-semibold text-[18px] text-napoli-espresso leading-tight">Manager / Owner</span>
          <span className="mt-1 block font-body text-[11px] text-napoli-espresso-soft">Manager or owner access</span>
        </span>
        <ArrowRight size={20} className="flex-shrink-0 transition-transform group-hover:translate-x-1" aria-hidden />
      </button>
    </div>

    <p className="mt-10 text-center">
      <Link
        to="/login"
        data-testid="role-picker-customer-link"
        className="font-body italic text-[12px] text-napoli-gold/80 hover:text-napoli-gold underline-offset-4 hover:underline"
      >
        ← Customer? Sign in here →
      </Link>
    </p>
  </div>
);

/* ============================================================ STATE 2 */

const StaffSkeleton = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="flex flex-col items-center gap-2 px-3 py-4 bg-napoli-espresso-soft border border-napoli-gold/15">
        <div className="h-[60px] w-[60px] rounded-full bg-napoli-espresso/60 animate-pulse" />
        <div className="h-4 w-16 bg-napoli-espresso/60 animate-pulse mt-2" />
        <div className="h-3 w-12 bg-napoli-espresso/60 animate-pulse" />
      </div>
    ))}
  </div>
);

const StaffPicker = ({ onPick, onBack }) => {
  const [staff, setStaff] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/pin-auth/staff-list");
        if (alive) setStaff(data.staff || []);
      } catch (_) {
        if (alive) setErr("We couldn't load the team list. Try again.");
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="staff-login-staff-picker" className="w-full">
      <p className="font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-gold text-center">
        Staff sign-in
      </p>
      <h1 className="mt-3 font-display font-bold text-[24px] text-napoli-cream text-center">
        Who&rsquo;s working?
      </h1>
      <p className="mt-2 font-body italic text-[13px] text-napoli-cream/70 text-center">
        Tap your name to continue.
      </p>

      <div className="mt-8">
        {err ? (
          <p data-testid="staff-picker-error" className="text-center font-body text-[13px] text-napoli-rose">
            {err}
          </p>
        ) : staff === null ? (
          <StaffSkeleton />
        ) : staff.length === 0 ? (
          <p className="text-center font-body italic text-[13px] text-napoli-cream/60">
            No staff seeded yet. Ask an admin to create accounts.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {staff.map((s) => (
              <button
                key={s.id}
                type="button"
                data-testid={`staff-card-${s.first_name.toLowerCase()}`}
                onClick={() => onPick(s)}
                className="flex flex-col items-center gap-2 px-3 py-4 bg-napoli-espresso-soft border border-napoli-gold/30 hover:border-napoli-gold hover:scale-[1.03] transition-all"
              >
                <span
                  aria-hidden
                  className="h-[60px] w-[60px] rounded-full bg-napoli-cream-warm border border-napoli-gold text-napoli-oxblood flex items-center justify-center font-display font-bold text-[20px]"
                >
                  {s.initials}
                </span>
                <span className="font-display font-semibold text-[16px] text-napoli-cream leading-tight">
                  {s.first_name}
                </span>
                <span className="font-body text-[11px] text-napoli-cream/60">
                  {s.last_name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onBack}
        data-testid="staff-picker-back"
        className="mt-10 inline-flex items-center gap-1 font-body text-[12px] tracking-[0.18em] uppercase text-napoli-cream/70 hover:text-napoli-gold"
      >
        <ChevronLeft size={14} /> Back
      </button>
    </div>
  );
};

/* =========================================================== STATE 3 / 4 */

/**
 * Shared PIN entry: 4 slots for staff, 6 for admin. Auto-submit on full.
 * Numeric keypad always rendered (POS-friendly tap targets).
 */
const PinEntry = ({
  length,
  onSubmit,
  submitting,
  shake,
  variant = "staff", // 'staff' | 'admin'
}) => {
  const [pin, setPin] = useState("");
  const inputRef = useRef(null);
  const submittedRef = useRef("");

  // Auto-submit when full, but only once per filled value (prevents repeat
  // submits if shake clears and re-fills).
  useEffect(() => {
    if (pin.length === length && submittedRef.current !== pin && !submitting) {
      submittedRef.current = pin;
      onSubmit(pin);
    }
  }, [pin, length, onSubmit, submitting]);

  // After parent shakes (signals a wrong attempt), clear pin so the user
  // can re-enter.
  useEffect(() => {
    if (shake) {
      setPin("");
      submittedRef.current = "";
      // Refocus the hidden input so the on-screen keypad still works
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [shake]);

  // Keep the hidden input focused so keyboard input works at all times
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleHiddenChange = (e) => {
    const next = (e.target.value || "").replace(/\D/g, "").slice(0, length);
    setPin(next);
  };

  const tap = (digit) => {
    if (submitting || pin.length >= length) return;
    setPin((p) => (p + digit).slice(0, length));
    inputRef.current?.focus();
  };

  const back = () => {
    if (submitting) return;
    setPin((p) => p.slice(0, -1));
    submittedRef.current = "";
    inputRef.current?.focus();
  };

  const enter = () => {
    if (submitting) return;
    if (pin.length === length && submittedRef.current !== pin) {
      submittedRef.current = pin;
      onSubmit(pin);
    }
  };

  const slotBorder =
    variant === "admin" ? "border-napoli-gold-light" : "border-napoli-gold/60";
  const slotFocusBorder =
    variant === "admin" ? "border-napoli-oxblood" : "border-napoli-oxblood";

  return (
    <div
      key={shake}
      className={`flex flex-col items-center w-full ${shake ? "animate-pin-shake" : ""}`}
      data-testid="pin-entry"
    >
      {/* Visible slots */}
      <div className="flex gap-2 sm:gap-3" onClick={() => inputRef.current?.focus()}>
        {Array.from({ length }).map((_, i) => {
          const filled = i < pin.length;
          const isCurrent = i === pin.length && pin.length < length;
          return (
            <span
              key={i}
              data-testid={`pin-slot-${i}`}
              data-filled={filled ? "1" : "0"}
              className={[
                "w-[52px] h-[64px] sm:w-[56px] sm:h-[68px] flex items-center justify-center",
                "bg-napoli-cream-warm font-mono font-semibold text-[24px] text-napoli-espresso",
                "border-2 transition-colors",
                isCurrent ? slotFocusBorder : slotBorder,
              ].join(" ")}
            >
              {filled ? "•" : ""}
            </span>
          );
        })}
      </div>

      {/* Hidden input for keyboard/paste support */}
      <input
        ref={inputRef}
        data-testid="pin-hidden-input"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        value={pin}
        onChange={handleHiddenChange}
        aria-label="PIN entry"
        maxLength={length}
        className="absolute opacity-0 pointer-events-none w-px h-px"
      />

      {/* Keypad */}
      <div className="mt-8 grid grid-cols-3 gap-3">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
          <button
            key={d}
            type="button"
            data-testid={`keypad-${d}`}
            onClick={() => tap(String(d))}
            disabled={submitting}
            className="w-[60px] h-[60px] sm:w-[64px] sm:h-[64px] bg-napoli-espresso-soft border border-napoli-gold/40 text-napoli-cream font-mono font-semibold text-[22px] hover:border-napoli-gold active:bg-napoli-espresso/80 transition-colors disabled:opacity-40"
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          data-testid="keypad-back"
          onClick={back}
          disabled={submitting || pin.length === 0}
          className="w-[60px] h-[60px] sm:w-[64px] sm:h-[64px] bg-napoli-espresso-soft border border-napoli-gold/40 text-napoli-cream flex items-center justify-center hover:border-napoli-gold active:bg-napoli-espresso/80 transition-colors disabled:opacity-30"
          aria-label="Backspace"
        >
          <Delete size={20} />
        </button>
        <button
          type="button"
          data-testid="keypad-0"
          onClick={() => tap("0")}
          disabled={submitting}
          className="w-[60px] h-[60px] sm:w-[64px] sm:h-[64px] bg-napoli-espresso-soft border border-napoli-gold/40 text-napoli-cream font-mono font-semibold text-[22px] hover:border-napoli-gold active:bg-napoli-espresso/80 transition-colors disabled:opacity-40"
        >
          0
        </button>
        <button
          type="button"
          data-testid="keypad-enter"
          onClick={enter}
          disabled={submitting || pin.length !== length}
          className="w-[60px] h-[60px] sm:w-[64px] sm:h-[64px] bg-napoli-oxblood border border-napoli-oxblood-dark text-napoli-cream flex items-center justify-center hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-30"
          aria-label="Submit"
        >
          {submitting ? <Loader2 size={20} className="animate-spin" /> : <ArrowRight size={20} />}
        </button>
      </div>
    </div>
  );
};

/* ================================================================ PAGE */

const StaffLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [stage, setStage] = useState("role"); // role | staff-pick | staff-pin | admin-pin
  const [chosenStaff, setChosenStaff] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [shake, setShake] = useState(0);

  const triggerShake = () => setShake((n) => n + 1);

  const submitStaffPin = async (pin) => {
    if (!chosenStaff) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/pin-auth/verify-staff-pin", {
        user_id: chosenStaff.id,
        pin,
      });
      window.localStorage.setItem(TOKEN_KEY, data.token);
      login(data.token, data.user);
      navigate(homeForRole(data.user?.role || "staff"), { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      const code = detail.error;
      if (code === "pin_locked") {
        const mins = Math.max(1, Math.round((detail.retry_after_seconds || 900) / 60));
        toast.error(`Too many wrong attempts. Try again in ${mins} minute${mins === 1 ? "" : "s"}.`);
        triggerShake();
        // Bounce back to staff picker so a different person can try
        setTimeout(() => {
          setChosenStaff(null);
          setStage("staff-pick");
        }, 1200);
      } else if (e?.response?.status === 429) {
        toast.error("Too many attempts from this device. Wait a few minutes.");
        triggerShake();
      } else {
        const remaining = detail.attempts_remaining;
        toast.error(
          remaining !== undefined
            ? `Wrong PIN. ${remaining} attempt${remaining === 1 ? "" : "s"} left.`
            : "Wrong PIN."
        );
        triggerShake();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitAdminPin = async (pin) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/pin-auth/verify-admin-pin", { pin });
      window.localStorage.setItem(TOKEN_KEY, data.token);
      login(data.token, data.user);
      navigate(homeForRole(data.user?.role || "admin"), { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      const code = detail.error;
      if (code === "pin_locked") {
        const mins = Math.max(1, Math.round((detail.retry_after_seconds || 900) / 60));
        toast.error(`Too many wrong attempts. Try again in ${mins} minute${mins === 1 ? "" : "s"}.`);
        triggerShake();
      } else if (e?.response?.status === 429) {
        toast.error("Too many attempts from this device. Wait a few minutes.");
        triggerShake();
      } else {
        const remaining = detail.attempts_remaining;
        toast.error(
          remaining !== undefined
            ? `Wrong code. ${remaining} attempt${remaining === 1 ? "" : "s"} left.`
            : "Wrong code."
        );
        triggerShake();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const containerKey = useMemo(() => stage + (chosenStaff?.id || ""), [stage, chosenStaff]);

  return (
    <main
      data-testid="staff-login-page"
      className="relative min-h-screen bg-napoli-espresso text-napoli-cream flex flex-col items-center px-6 pt-12 pb-12 overflow-hidden"
    >
      <PaperGrain />
      <div className="relative z-10 w-full max-w-md flex flex-col items-center">
        {stage === "role" ? (
          <BrandMarkLink size="md" theme="dark" testId="staff-login-brand-link" ariaLabel="Napoli — home" />
        ) : (
          <BrandMarkLink size="sm" theme="dark" testId="staff-login-brand-link-sm" ariaLabel="Napoli — home" />
        )}

        <div
          key={containerKey}
          className="mt-10 w-full animate-in fade-in duration-200"
        >
          {stage === "role" ? (
            <RolePicker
              onPickStaff={() => setStage("staff-pick")}
              onPickAdmin={() => setStage("admin-pin")}
            />
          ) : null}

          {stage === "staff-pick" ? (
            <StaffPicker
              onPick={(s) => {
                setChosenStaff(s);
                setStage("staff-pin");
              }}
              onBack={() => setStage("role")}
            />
          ) : null}

          {stage === "staff-pin" && chosenStaff ? (
            <div data-testid="staff-login-staff-pin" className="w-full flex flex-col items-center">
              <div className="flex items-center gap-3 px-4 py-3 bg-napoli-espresso-soft border border-napoli-gold/30">
                <span
                  aria-hidden
                  className="h-10 w-10 rounded-full bg-napoli-cream-warm border border-napoli-gold text-napoli-oxblood flex items-center justify-center font-display font-bold text-[14px]"
                >
                  {(chosenStaff.first_name?.[0] || "") + (chosenStaff.last_name?.[0] || "")}
                </span>
                <span>
                  <span className="block font-display font-semibold text-[14px] text-napoli-cream leading-tight">
                    {chosenStaff.first_name} {chosenStaff.last_name}
                  </span>
                  <span className="block font-body text-[10px] tracking-[0.22em] uppercase text-napoli-gold">
                    Staff
                  </span>
                </span>
              </div>

              <p className="mt-8 font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-gold text-center">
                Enter your PIN
              </p>
              <h1 className="mt-2 font-display font-bold text-[22px] text-napoli-cream text-center">
                Hello, {chosenStaff.first_name}.
              </h1>
              <p className="mt-2 font-body italic text-[13px] text-napoli-cream/70 text-center">
                4-digit PIN to continue.
              </p>

              <div className="mt-8">
                <PinEntry
                  length={4}
                  onSubmit={submitStaffPin}
                  submitting={submitting}
                  shake={shake}
                  variant="staff"
                />
              </div>

              <p className="mt-6 font-body italic text-[12px] text-napoli-cream/50">
                Wrong PIN? Ask your manager.
              </p>

              <button
                type="button"
                onClick={() => {
                  setChosenStaff(null);
                  setStage("staff-pick");
                }}
                data-testid="staff-pin-back"
                className="mt-6 inline-flex items-center gap-1 font-body text-[12px] tracking-[0.18em] uppercase text-napoli-cream/70 hover:text-napoli-gold"
              >
                <ChevronLeft size={14} /> Back
              </button>
            </div>
          ) : null}

          {stage === "admin-pin" ? (
            <div data-testid="staff-login-admin-pin" className="w-full flex flex-col items-center">
              <div className="flex items-center justify-center h-14 w-14 rounded-full bg-napoli-cream-warm border-2 border-napoli-gold-light">
                <Crown size={26} className="text-napoli-oxblood" />
              </div>
              <p className="mt-6 font-body font-semibold text-[11px] tracking-[0.3em] uppercase text-napoli-gold-light text-center">
                Manager access
              </p>
              <h1 className="mt-2 font-display font-bold text-[22px] text-napoli-cream text-center">
                Enter admin code.
              </h1>
              <p className="mt-2 font-body italic text-[13px] text-napoli-cream/70 text-center">
                6-digit code to continue.
              </p>

              <div className="mt-8">
                <PinEntry
                  length={6}
                  onSubmit={submitAdminPin}
                  submitting={submitting}
                  shake={shake}
                  variant="admin"
                />
              </div>

              <button
                type="button"
                onClick={() => setStage("role")}
                data-testid="admin-pin-back"
                className="mt-8 inline-flex items-center gap-1 font-body text-[12px] tracking-[0.18em] uppercase text-napoli-cream/70 hover:text-napoli-gold"
              >
                <ChevronLeft size={14} /> Back
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <style>{`
        @keyframes pin-shake-anim {
          0%,100% { transform: translateX(0); }
          15% { transform: translateX(-8px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-6px); }
          60% { transform: translateX(6px); }
          75% { transform: translateX(-3px); }
        }
        .animate-pin-shake { animation: pin-shake-anim 420ms ease-in-out; }
      `}</style>
    </main>
  );
};

export default StaffLoginPage;
